import { observer } from 'mobx-react'
import { useEffect } from 'react'
import type { RouteProps } from 'react-router'
import { matchPath, Redirect, Route, useLocation } from 'react-router'

import commonRepo from '@/repository/common.repo'
import { toastFailure } from '@/utils/toast'

import { getEntrancePage, RouteName } from './RouteName'
import { ROUTE_RESTRICTION_MESSAGE_MAP } from './routeRestrictionMessage'

type Props = RouteProps & {
  redirectTo?: string
}

// 진입 페이지 자체가 차단 라우트면(내신준비기간의 내신관 등) 다시 진입 페이지로 보내는 순환이 생긴다
const getFallbackRedirect = () => {
  const entrance = getEntrancePage({
    isSchoolflat: commonRepo.isSchoolflat,
    isSeoltab: commonRepo.isSeoltab,
  })
  return commonRepo.permission.isRouteAllowed(entrance) ? entrance : RouteName.worksheet
}

/**
 * featurePermissions 기반 route 접근 제어 컴포넌트
 *
 * TEACHER_FEATURE_CONFIG의 gatedRoutes에 등록된 경로는
 * 해당 feature가 DENY일 때 redirectTo로 리다이렉트한다.
 * 매핑되지 않은 경로는 항상 통과한다.
 */
const PermissionGuardRoute = observer(
  ({ redirectTo = getFallbackRedirect(), ...routeProps }: Props) => {
    const { pathname } = useLocation()

    const paths: string[] = Array.isArray(routeProps.path)
      ? routeProps.path
      : routeProps.path !== undefined
        ? [routeProps.path]
        : []

    const matchedPath = paths.find((p) =>
      matchPath(pathname, {
        path: p,
        exact: routeProps.exact,
        strict: routeProps.strict,
        sensitive: routeProps.sensitive,
      }),
    )

    const isBlocked =
      matchedPath !== undefined && !commonRepo.permission.isRouteAllowed(matchedPath)
    // 학원그룹 학습지·교재는 학원이 그룹에 속해 있을 때만 안내 토스트를 띄운다.
    // groupId가 없으면 탭 자체가 노출되지 않으므로 안내 토스트도 띄우지 않고 redirect만 한다.
    const isAcademyGroupRoute =
      matchedPath === RouteName.academyGroupShareScopeWorksheet ||
      matchedPath === RouteName.academyGroupShareScopeWorkbook
    const restrictionMessage =
      matchedPath && !(isAcademyGroupRoute && !commonRepo.academy?.groupId)
        ? ROUTE_RESTRICTION_MESSAGE_MAP[
            matchedPath as keyof typeof ROUTE_RESTRICTION_MESSAGE_MAP
          ]?.()
        : undefined

    useEffect(() => {
      if (isBlocked && restrictionMessage) {
        toastFailure(restrictionMessage)
      }
    }, [isBlocked, restrictionMessage])

    if (isBlocked) {
      return <Redirect to={redirectTo} />
    }

    return <Route {...routeProps} />
  },
)

export default PermissionGuardRoute
